import { getErrorFromErrorResponse, isErrorResponse, } from '@standardnotes/responses';
import { KeyParamsOrigination } from '@standardnotes/common';
import { UuidGenerator } from '@standardnotes/utils';
import { c, msgid } from 'ttag';
import * as Messages from '../Strings/Messages';
import { InfoStrings } from '../Strings/InfoStrings';
import { Challenge, ChallengePrompt, ChallengeReason, ChallengeValidation, } from '../Challenge';
import { AbstractService } from '../Service/AbstractService';
import { DeinitSource } from '../Application/DeinitSource';
import { StoragePersistencePolicies } from '../Storage/StorageTypes';
import { AccountEvent } from './AccountEvent';
const cleanedEmailString = (email) => {
    return email.trim().toLowerCase();
};
export class UserService extends AbstractService {
    constructor(sessions, sync, storage, items, encryption, alerts, challenges, protections, userApi, _reencryptTypeAItems, _decryptErroredPayloads, internalEventBus) {
        super(internalEventBus);
        this.sessions = sessions;
        this.sync = sync;
        this.storage = storage;
        this.items = items;
        this.encryption = encryption;
        this.alerts = alerts;
        this.challenges = challenges;
        this.protections = protections;
        this.userApi = userApi;
        this._reencryptTypeAItems = _reencryptTypeAItems;
        this._decryptErroredPayloads = _decryptErroredPayloads;
        this.internalEventBus = internalEventBus;
        this.signingIn = false;
        this.registering = false;
        this.MINIMUM_PASSCODE_LENGTH = 1;
        this.MINIMUM_PASSWORD_LENGTH = 8;
    }
    deinit() {
        super.deinit();
        this.sessions = undefined;
        this.sync = undefined;
        this.storage = undefined;
        this.items = undefined;
        this.encryption = undefined;
        this.alerts = undefined;
        this.challenges = undefined;
        this.protections = undefined;
        this.userApi = undefined;
        this._reencryptTypeAItems = undefined;
        this._decryptErroredPayloads = undefined;
    }
    async handleEvent(event) {
        if (event.type === AccountEvent.SignedInOrRegistered) {
            const payload = event.payload.payload;
            this.sync.resetSyncState();
            await this.storage.setPersistencePolicy(payload.ephemeral ? StoragePersistencePolicies.Ephemeral : StoragePersistencePolicies.Default);
            if (payload.mergeLocal) {
                await this.sync.markAllItemsAsNeedingSyncAndPersist();
            }
            else {
                void this.items.removeAllItemsFromMemory();
                await this.clearDatabase();
            }
            this.unlockSyncing();
            const syncPromise = this.sync
                .downloadFirstSync(1000, {
                checkIntegrity: payload.checkIntegrity,
                awaitAll: payload.awaitSync,
            })
                .then(() => {
                if (!payload.awaitSync) {
                    void this._decryptErroredPayloads.execute();
                }
            });
            if (payload.awaitSync) {
                await syncPromise;
                await this._decryptErroredPayloads.execute();
            }
        }
    }
    get user() {
        return this.sessions.getUser();
    }
    get sureUser() {
        return this.sessions.getSureUser();
    }
    getUserUuid() {
        return this.sessions.userUuid;
    }
    isSignedIn() {
        return this.sessions.isSignedIn();
    }
    /**
     *  @param mergeLocal  Whether to merge existing offline data into account. If false,
     *                     any pre-existing data will be fully deleted upon success.
     */
    async register(email, password, hvmToken, ephemeral = false, mergeLocal = true) {
        if (this.encryption.hasAccount()) {
            throw Error('Tried to register when an account already exists.');
        }
        if (this.registering) {
            throw Error('Already registering.');
        }
        this.registering = true;
        try {
            this.lockSyncing();
            const response = await this.sessions.register(email, password, hvmToken, ephemeral);
            await this.notifyEventSync(AccountEvent.SignedInOrRegistered, {
                payload: {
                    ephemeral,
                    mergeLocal,
                    awaitSync: true,
                    checkIntegrity: false,
                },
            });
            this.registering = false;
            return response;
        }
        catch (error) {
            this.unlockSyncing();
            this.registering = false;
            throw error;
        }
    }
    /**
     * @param mergeLocal  Whether to merge existing offline data into account.
     * If false, any pre-existing data will be fully deleted upon success.
     */
    async signIn(email, password, strict = false, ephemeral = false, mergeLocal = true, awaitSync = false, hvmToken) {
        if (this.encryption.hasAccount()) {
            throw Error('Tried to sign in when an account already exists.');
        }
        if (this.signingIn) {
            throw Error('Already signing in.');
        }
        this.signingIn = true;
        try {
            /** Prevent a timed sync from occuring while signing in. */
            this.lockSyncing();
            const { response } = await this.sessions.signIn(email, password, strict, ephemeral, undefined, hvmToken);
            if (!isErrorResponse(response)) {
                const notifyingFunction = awaitSync ? this.notifyEventSync.bind(this) : this.notifyEvent.bind(this);
                await notifyingFunction(AccountEvent.SignedInOrRegistered, {
                    payload: {
                        mergeLocal,
                        awaitSync,
                        ephemeral,
                        checkIntegrity: true,
                    },
                });
            }
            else {
                this.unlockSyncing();
            }
            return response;
        }
        finally {
            this.signingIn = false;
        }
    }
    async deleteAccount() {
        const { success, challengeResponse } = await this.protections.authorizeAccountDeletion();
        if (!success) {
            return {
                error: true,
                message: Messages.INVALID_PASSWORD(),
            };
        }
        const uuid = this.sessions.getSureUser().uuid;
        const password = challengeResponse === null || challengeResponse === void 0 ? void 0 : challengeResponse.getValueForType(ChallengeValidation.AccountPassword).value;
        const currentRootKey = await this.encryption.computeRootKey(password, this.encryption.getRootKeyParams());
        const serverPassword = currentRootKey.serverPassword;
        const response = await this.userApi.deleteAccount({ userUuid: uuid, serverPassword: serverPassword });
        if (isErrorResponse(response)) {
            return {
                error: true,
                message: getErrorFromErrorResponse(response).message,
            };
        }
        await this.signOut(true);
        if (this.alerts) {
            void this.alerts.alert(InfoStrings.AccountDeleted);
        }
        return {
            error: false,
        };
    }
    async submitUserRequest(requestType) {
        const userUuid = this.sessions.getSureUser().uuid;
        try {
            const result = await this.userApi.submitUserRequest({
                userUuid,
                requestType,
            });
            if (isErrorResponse(result)) {
                return false;
            }
            return result.data.success;
        }
        catch {
            return false;
        }
    }
    /**
     * A sign in request that occurs while the user was previously signed in, to correct
     * for missing keys or storage values. Unlike regular sign in, this doesn't worry about
     * performing one of marking all items as needing sync or deleting all local data.
     */
    async correctiveSignIn(rootKey) {
        this.lockSyncing();
        const response = await this.sessions.bypassChecksAndSignInWithRootKey(rootKey.keyParams.identifier, rootKey, false);
        if (!isErrorResponse(response)) {
            await this.notifyEvent(AccountEvent.SignedInOrRegistered, {
                payload: {
                    mergeLocal: true,
                    awaitSync: true,
                    ephemeral: false,
                    checkIntegrity: true,
                },
            });
        }
        this.unlockSyncing();
        return response;
    }
    /**
     * @param passcode - Changing the account password or email requires the local
     * passcode if configured (to rewrap the account key with passcode). If the passcode
     * is not passed in, the user will be prompted for the passcode. However if the consumer
     * already has reference to the passcode, they can pass it in here so that the user
     * is not prompted again.
     */
    async changeCredentials(parameters) {
        const result = await this.performCredentialsChange(parameters);
        if (result.error) {
            void this.alerts.alert(result.error.message);
        }
        return result;
    }
    async signOut(force = false, source = DeinitSource.SignOut) {
        const performSignOut = async () => {
            await this.sessions.signOut();
            await this.encryption.deleteWorkspaceSpecificKeyStateFromDevice();
            await this.storage.clearAllData();
            await this.notifyEvent(AccountEvent.SignedOut, { payload: { source } });
        };
        if (force) {
            await performSignOut();
            return;
        }
        const dirtyItems = this.items.getDirtyItems();
        if (dirtyItems.length > 0) {
            const dirtyItemCount = dirtyItems.length;
            const didConfirm = await this.alerts.confirm(c('B1.Account.Session.Confirmation').ngettext(msgid `There is ${dirtyItemCount} item with unsynced changes. If you sign out, these changes will be lost forever. Are you sure you want to sign out?`, `There are ${dirtyItemCount} items with unsynced changes. If you sign out, these changes will be lost forever. Are you sure you want to sign out?`, dirtyItemCount));
            if (didConfirm) {
                await performSignOut();
            }
        }
        else {
            await performSignOut();
        }
    }
    async updateAccountWithFirstTimeKeyPair() {
        if (!this.sessions.isUserMissingKeyPair()) {
            throw Error('Cannot update account with first time keypair if user already has a keypair');
        }
        const result = await this.performProtocolUpgrade();
        return result;
    }
    async performProtocolUpgrade() {
        const hasPasscode = this.encryption.hasPasscode();
        const hasAccount = this.encryption.hasAccount();
        const prompts = [];
        if (hasPasscode) {
            prompts.push(new ChallengePrompt(ChallengeValidation.LocalPasscode, undefined, Messages.ChallengeStrings.LocalPasscodePlaceholder));
        }
        if (hasAccount) {
            prompts.push(new ChallengePrompt(ChallengeValidation.AccountPassword, undefined, Messages.ChallengeStrings.AccountPasswordPlaceholder));
        }
        const challenge = new Challenge(prompts, ChallengeReason.ProtocolUpgrade, true);
        const response = await this.challenges.promptForChallengeResponse(challenge);
        if (!response) {
            return { canceled: true };
        }
        const dismissBlockingDialog = await this.alerts.blockingDialog(Messages.DO_NOT_CLOSE_APPLICATION(), Messages.UPGRADING_ENCRYPTION());
        try {
            let passcode;
            if (hasPasscode) {
                /* Upgrade passcode version */
                const value = response.getValueForType(ChallengeValidation.LocalPasscode);
                passcode = value.value;
            }
            if (hasAccount) {
                /* Upgrade account version */
                const value = response.getValueForType(ChallengeValidation.AccountPassword);
                const password = value.value;
                const changeResponse = await this.changeCredentials({
                    currentPassword: password,
                    newPassword: password,
                    passcode,
                    origination: KeyParamsOrigination.ProtocolUpgrade,
                    validateNewPasswordStrength: false,
                });
                if (changeResponse === null || changeResponse === void 0 ? void 0 : changeResponse.error) {
                    return { error: changeResponse.error };
                }
            }
            if (hasPasscode) {
                /* Upgrade passcode version */
                await this.removePasscodeWithoutWarning();
                await this.setPasscodeWithoutWarning(passcode, KeyParamsOrigination.ProtocolUpgrade);
            }
            return { success: true };
        }
        catch (error) {
            return { error: error };
        }
        finally {
            dismissBlockingDialog();
        }
    }
    async addPasscode(passcode) {
        if (passcode.length < this.MINIMUM_PASSCODE_LENGTH) {
            return false;
        }
        if (!(await this.protections.authorizeAddingPasscode())) {
            return false;
        }
        const dismissBlockingDialog = await this.alerts.blockingDialog(Messages.DO_NOT_CLOSE_APPLICATION(), Messages.SETTING_PASSCODE());
        try {
            await this.setPasscodeWithoutWarning(passcode, KeyParamsOrigination.PasscodeCreate);
            return true;
        }
        finally {
            dismissBlockingDialog();
        }
    }
    async removePasscode() {
        if (!(await this.protections.authorizeRemovingPasscode())) {
            return false;
        }
        const dismissBlockingDialog = await this.alerts.blockingDialog(Messages.DO_NOT_CLOSE_APPLICATION(), Messages.REMOVING_PASSCODE());
        try {
            await this.removePasscodeWithoutWarning();
            return true;
        }
        finally {
            dismissBlockingDialog();
        }
    }
    /**
     * @returns whether the passcode was successfuly changed or not
     */
    async changePasscode(newPasscode, origination = KeyParamsOrigination.PasscodeChange) {
        if (newPasscode.length < this.MINIMUM_PASSCODE_LENGTH) {
            return false;
        }
        if (!(await this.protections.authorizeChangingPasscode())) {
            return false;
        }
        const dismissBlockingDialog = await this.alerts.blockingDialog(Messages.DO_NOT_CLOSE_APPLICATION(), origination === KeyParamsOrigination.ProtocolUpgrade
            ? Messages.ProtocolUpgradeStrings.UpgradingPasscode
            : Messages.CHANGING_PASSCODE());
        try {
            await this.removePasscodeWithoutWarning();
            await this.setPasscodeWithoutWarning(newPasscode, origination);
            return true;
        }
        finally {
            dismissBlockingDialog();
        }
    }
    async populateSessionFromDemoShareToken(token) {
        await this.sessions.populateSessionFromDemoShareToken(token);
        await this.notifyEvent(AccountEvent.SignedInOrRegistered, {
            payload: {
                ephemeral: false,
                mergeLocal: false,
                checkIntegrity: false,
                awaitSync: true,
            },
        });
    }
    async setPasscodeWithoutWarning(passcode, origination) {
        const identifier = UuidGenerator.GenerateUuid();
        const key = await this.encryption.createRootKey(identifier, passcode, origination);
        await this.encryption.setNewRootKeyWrapper(key);
        await this.rewriteItemsKeys();
        await this.sync.sync();
    }
    async removePasscodeWithoutWarning() {
        await this.encryption.removePasscode();
        await this.rewriteItemsKeys();
    }
    /**
     * Allows items keys to be rewritten to local db on local credential status change,
     * such as if passcode is added, changed, or removed.
     * This allows IndexedDB unencrypted logs to be deleted
     * `deletePayloads` will remove data from backing store,
     * but not from working memory See:
     * https://github.com/standardnotes/desktop/issues/131
     */
    async rewriteItemsKeys() {
        const itemsKeys = this.items.getDisplayableItemsKeys();
        const payloads = itemsKeys.map((key) => key.payloadRepresentation());
        await this.storage.deletePayloads(payloads);
        await this.sync.persistPayloads(payloads);
    }
    lockSyncing() {
        this.sync.lockSyncing();
    }
    unlockSyncing() {
        this.sync.unlockSyncing();
    }
    clearDatabase() {
        return this.storage.clearAllPayloads();
    }
    async performCredentialsChange(parameters) {
        var _a;
        const { wrappingKey, canceled } = await this.challenges.getWrappingKeyIfApplicable(parameters.passcode);
        if (canceled) {
            return { error: Error(Messages.CredentialsChangeStrings.PasscodeRequired) };
        }
        if (parameters.newPassword !== undefined && parameters.validateNewPasswordStrength) {
            if (parameters.newPassword.length < this.MINIMUM_PASSWORD_LENGTH) {
                return {
                    error: Error(Messages.InsufficientPasswordMessage(this.MINIMUM_PASSWORD_LENGTH)),
                };
            }
        }
        const accountPasswordValidation = await this.encryption.validateAccountPassword(parameters.currentPassword);
        if (!accountPasswordValidation.valid) {
            return {
                error: Error(Messages.INVALID_PASSWORD()),
            };
        }
        const newEmail = parameters.newEmail ? cleanedEmailString(parameters.newEmail) : undefined;
        const user = this.sessions.getUser();
        const currentEmail = user.email;
        const { currentRootKey, newRootKey } = await this.recomputeRootKeysForCredentialChange({
            currentPassword: parameters.currentPassword,
            currentEmail,
            origination: parameters.origination,
            newEmail: newEmail,
            newPassword: parameters.newPassword,
        });
        this.lockSyncing();
        const { response } = await this.sessions.changeCredentials({
            currentServerPassword: currentRootKey.serverPassword,
            newRootKey: newRootKey,
            wrappingKey,
            newEmail: newEmail,
        });
        this.unlockSyncing();
        if (isErrorResponse(response)) {
            return { error: Error((_a = response.data.error) === null || _a === void 0 ? void 0 : _a.message) };
        }
        const rollback = await this.encryption.createNewItemsKeyWithRollback();
        await this._reencryptTypeAItems.execute();
        await this.sync.sync({ awaitAll: true });
        const defaultItemsKey = this.encryption.getSureDefaultItemsKey();
        const itemsKeyWasSynced = !defaultItemsKey.neverSynced;
        if (!itemsKeyWasSynced) {
            await this.sessions.changeCredentials({
                currentServerPassword: newRootKey.serverPassword,
                newRootKey: currentRootKey,
                wrappingKey,
            });
            await this._reencryptTypeAItems.execute();
            await rollback();
            await this.sync.sync({ awaitAll: true });
            return { error: Error(Messages.CredentialsChangeStrings.Failed) };
        }
        return {};
    }
    async recomputeRootKeysForCredentialChange(parameters) {
        var _a, _b;
        const currentRootKey = await this.encryption.computeRootKey(parameters.currentPassword, this.encryption.getRootKeyParams());
        const newRootKey = await this.encryption.createRootKey((_a = parameters.newEmail) !== null && _a !== void 0 ? _a : parameters.currentEmail, (_b = parameters.newPassword) !== null && _b !== void 0 ? _b : parameters.currentPassword, parameters.origination);
        return {
            currentRootKey,
            newRootKey,
        };
    }
}
